// ui/AutoHeight.js
import React, { useRef, useEffect, useRef as useRefAlias } from "react";

/**
 * A wrapper to animate the transition of the height of a container when the container's contents change.
 * It observes the child's height (or change in content), then animates the expansion or collapse or the parent container.
 */
export default function AutoHeight({
  children,
  duration = 100, // ms
  easing = "cubic-bezier(0.2, 0.8, 0.2, 1)",
  className = "",
}) {
  const outerRef = useRef(null);
  const innerRef = useRef(null);
  const animRef = useRef(null);
  const rafRef = useRef(0);

  useEffect(() => {
    const outer = outerRef.current;
    const inner = innerRef.current;
    if (!outer || !inner) return;

    // Ensure we start with correct height (no jump)
    outer.style.maxHeight = inner.offsetHeight + "px";

    let frame = 0;
    const ro = new ResizeObserver(() => {
      cancelAnimationFrame(rafRef.current);
      rafRef.current = requestAnimationFrame(() => {
        if (!outer || !inner) return;
        const from = outer.getBoundingClientRect().height;
        const to = inner.offsetHeight;

        // Cancel any in-flight animation
        if (animRef.current) {
          outer.removeEventListener("transitionend", animRef.current);
          animRef.current = null;
        }

        // Lock current height, then animate to new height
        outer.style.transition = "none";
        outer.style.maxHeight = from + "px";
        // next frame
        frame = requestAnimationFrame(() => {
          outer.style.transition = `max-height ${duration}ms ${easing}`;
          outer.style.maxHeight = to + "px";

          const onEnd = (e) => {
            if (e.propertyName !== "max-height") return;
            // Let it grow naturally after expand (so inner links/selectable text work)
            outer.style.transition = "none";
            // If fully expanded, allow natural height; if not, keep maxHeight
            if (to >= from) {
              outer.style.maxHeight = "none";
            } else {
              outer.style.maxHeight = inner.offsetHeight + "px";
            }
          };
          animRef.current = onEnd;
          outer.addEventListener("transitionend", onEnd, { once: true });
        });
      });
    });

    ro.observe(inner);
    return () => {
      ro.disconnect();
      cancelAnimationFrame(rafRef.current);
      cancelAnimationFrame(frame);
      if (animRef.current)
        outer.removeEventListener("transitionend", animRef.current);
    };
  }, [duration, easing]);

  return (
    <div ref={outerRef} className={className} style={{ overflow: "hidden" }}>
      <div ref={innerRef}>{children}</div>
    </div>
  );
}
